import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import './KycKybLanding.css';

interface KycKybLandingProps {
  theme: any;
}

const KycKybLanding: React.FC<KycKybLandingProps> = ({ theme }) => {
  const [userType, setUserType] = useState<string | null>(null);
  const navigate = useNavigate();

  // Apply theme class based on userType and listen for changes
  useEffect(() => {
    const applyTheme = () => {
      const currentUserType = localStorage.getItem('userType');
      const userLogged = localStorage.getItem('userlogged');
      
      // Update state to trigger re-render only if changed
      setUserType(prev => {
        if (prev !== currentUserType) {
          return currentUserType;
        }
        return prev;
      });
      
      // Determine theme class based on userType or userLogged
      let themeClass = 'theme-normal'; // Default to green
      
      if (currentUserType && currentUserType !== 'Indexx Exchange') {
        // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (currentUserType === 'CaptainBee' || userLogged === 'captain') {
        themeClass = 'theme-captain'; // Yellow for CaptainBee
      } else if (currentUserType === 'HoneyUser' || userLogged === 'honeyb') {
        themeClass = 'theme-honeyb'; // Yellow for HoneyBee
      } else if (userLogged === 'normal' || currentUserType === 'Indexx Exchange') {
        themeClass = 'theme-normal'; // Green for normal users
      }
      
      // Apply theme class to document body and root
      document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.body.classList.add(themeClass);
      document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      document.documentElement.classList.add(themeClass);
      
      // Also apply to root element to ensure CSS variables are available
      const root = document.getElementById('root');
      if (root) {
        root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
        root.classList.add(themeClass);
      }
    };

    // Apply theme on mount
    applyTheme();

    // Listen for storage changes (when userType is updated)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'userType' || e.key === 'userlogged') {
        applyTheme();
      }
    };

    // Listen for custom userType change event
    const handleUserTypeChange = () => {
      applyTheme();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userTypeChanged', handleUserTypeChange);

    // Poll for userType changes (in case localStorage is updated in same tab)
    let lastUserType = localStorage.getItem('userType');
    const pollInterval = setInterval(() => {
      const currentUserType = localStorage.getItem('userType');
      if (currentUserType !== lastUserType) {
        lastUserType = currentUserType;
        applyTheme();
      }
    }, 500); // Check every 500ms

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userTypeChanged', handleUserTypeChange);
      clearInterval(pollInterval);
    };
  }, []); // Empty dependency array - only run on mount

  return (
    <div className="kyc-kyb-landing">
      <div className="landing-container">
        {/* Hero Section */}
        <div className="landing-header">
          <div
            className="go-back-link"
            onClick={() =>
              window.history.length > 2 ? navigate(-1) : navigate('/')
            }
            style={{ color: theme.palette.text.secondary }}
          >
            <ArrowBackIcon fontSize="small" />
            Go Back
          </div>
          <div className="header-badge">Identity Verification</div>
          <h1 className="landing-title" style={{ color: theme.palette.text.primary }}>
            KYC & KYB Verification
          </h1>
          <p className="landing-subtitle" style={{ color: theme.palette.text.secondary }}>
            Secure your account and unlock advanced features with identity verification
          </p>
        </div>

        <div className="landing-content">
          {/* KYC/KYB Cards Section */}
          <div className="info-section">
            <div className="section-label">What You Need to Know</div>
            <div className="info-cards-wrapper">
              <div className="info-card">
                <div className="info-card-icon-wrapper">
                  <div className="info-card-icon">👤</div>
                </div>
                <div className="info-card-content">
                  <h3 className="info-card-title" style={{ color: theme.palette.text.primary }}>
                    KYC
                  </h3>
                  <p className="info-card-full-name" style={{ color: theme.palette.text.secondary }}>
                    Know Your Customer
                  </p>
                  <p className="info-card-subtitle" style={{ color: theme.palette.text.secondary }}>
                    For Individual Users
                  </p>
                  <p className="info-card-text" style={{ color: theme.palette.text.secondary }}>
                    Verifies your identity through government documents and personal information. 
                    Required for personal accounts to ensure security and compliance.
                  </p>
                </div>
              </div>

              <div className="info-card">
                <div className="info-card-icon-wrapper">
                  <div className="info-card-icon">🏢</div>
                </div>
                <div className="info-card-content">
                  <h3 className="info-card-title" style={{ color: theme.palette.text.primary }}>
                    KYB
                  </h3>
                  <p className="info-card-full-name" style={{ color: theme.palette.text.secondary }}>
                    Know Your Business
                  </p>
                  <p className="info-card-subtitle" style={{ color: theme.palette.text.secondary }}>
                    For Business Accounts
                  </p>
                  <p className="info-card-text" style={{ color: theme.palette.text.secondary }}>
                    Verifies company information, registration documents, and authorized representatives. 
                    Required for business accounts to ensure legitimacy.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why Section */}
          <div className="why-section">
            <div className="section-label">Why It Matters</div>
            <div className="why-grid">
              <div className="why-item">
                <div className="why-icon-wrapper">
                  <div className="why-icon">🛡️</div>
                </div>
                <p className="why-text" style={{ color: theme.palette.text.secondary }}>
                  Regulatory compliance with AML/CTF requirements
                </p>
              </div>
              <div className="why-item">
                <div className="why-icon-wrapper">
                  <div className="why-icon">🔒</div>
                </div>
                <p className="why-text" style={{ color: theme.palette.text.secondary }}>
                  Enhanced security and fraud prevention
                </p>
              </div>
              <div className="why-item">
                <div className="why-icon-wrapper">
                  <div className="why-icon">🌍</div>
                </div>
                <p className="why-text" style={{ color: theme.palette.text.secondary }}>
                  Builds trust in the crypto ecosystem
                </p>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="benefits-section">
            <div className="section-label">What You Get</div>
            <div className="benefits-grid">
              <div className="benefit-card">
                <div className="benefit-icon-wrapper">
                  <div className="benefit-icon">💰</div>
                </div>
                <h4 className="benefit-title" style={{ color: theme.palette.text.primary }}>
                  Higher Limits
                </h4>
                <p className="benefit-text" style={{ color: theme.palette.text.secondary }}>
                  Higher deposit and withdrawal limits
                </p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon-wrapper">
                  <div className="benefit-icon">⚡</div>
                </div>
                <h4 className="benefit-title" style={{ color: theme.palette.text.primary }}>
                  Advanced Features
                </h4>
                <p className="benefit-text" style={{ color: theme.palette.text.secondary }}>
                  Access to advanced trading features
                </p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon-wrapper">
                  <div className="benefit-icon">🎯</div>
                </div>
                <h4 className="benefit-title" style={{ color: theme.palette.text.primary }}>
                  Priority Support
                </h4>
                <p className="benefit-text" style={{ color: theme.palette.text.secondary }}>
                  Priority customer support
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KycKybLanding;

